import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import ApplicationNotificationsBell from './ApplicationNotificationsBell';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalSearch } from '../search';
import {
  Menu,
  X,
  Truck,
  BarChart3,
  Users,
  Settings,
  Bell,
  User,
  LogOut,
  Search,
  Shield,
  Archive,
  DollarSign,
  Package
} from 'lucide-react';

const BRAND_NAME = 'Himil Fleet';
const BRAND_LOGO_SRC = '/Himil-logo.png';

const navigation = [
  { name: 'Dashboard', href: '/', icon: BarChart3 },
  { name: 'Vehicles', href: '/vehicles', icon: Truck },
  { name: 'Drivers', href: '/drivers', icon: Users },
  { name: 'Loads', href: '/loads', icon: Package },
  { name: 'Financial', href: '/financial', icon: DollarSign },
  { name: 'Compliance', href: '/compliance', icon: Shield },
  { name: 'Documents', href: '/documents', icon: Archive },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userData, logout, hasPermission } = useAuth();
  const canReviewApplications = ['admin', 'manager', 'super_admin'].includes(userData?.role);

  // Mock data for global search - in a real app, this would come from context/API
  const searchData = {
    drivers: [
      { id: 1, name: 'John Smith', licenseStatus: 'Active', hosStatus: 'Available' },
      { id: 2, name: 'Sarah Johnson', licenseStatus: 'Active', hosStatus: 'Driving' },
      { id: 3, name: 'Mike Wilson', licenseStatus: 'Active', hosStatus: 'Off Duty' },
      { id: 4, name: 'Lisa Brown', licenseStatus: 'Active', hosStatus: 'Driving' },
    ],
    vehicles: [
      { id: 1, vehicleNumber: 'T-001', make: 'Kenworth', model: 'T680', status: 'active', assignedDriver: 'John Smith' },
      { id: 2, vehicleNumber: 'T-002', make: 'Freightliner', model: 'Cascadia', status: 'maintenance', assignedDriver: 'Sarah Johnson' },
      { id: 3, vehicleNumber: 'TR-001', make: 'Great Dane', model: 'Dry Van', status: 'active', assignedDriver: 'Mike Wilson' },
    ],
    documents: [
      { name: 'CDL License - John Smith', type: 'License', driverName: 'John Smith' },
      { name: 'Medical Certificate - Sarah Johnson', type: 'Medical', driverName: 'Sarah Johnson' },
      { name: 'Insurance Policy - Company', type: 'Insurance', driverName: null },
    ]
  };

  const handleGlobalSearchNavigate = (type, data) => {
    switch (type) {
      case 'driver':
        navigate('/drivers');
        break;
      case 'vehicle':
        navigate('/vehicles');
        break;
      case 'document':
        navigate('/documents');
        break;
      default:
        console.log('Navigate to:', type, data);
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter navigation based on permissions
  const filteredNavigation = navigation.filter(item => {
    const permission = item.href === '/' ? 'dashboard' : item.href.substring(1);
    return hasPermission(permission);
  });

  return (
    <div className="h-screen flex bg-slate-100 overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-slate-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar-gradient text-white transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-white/20 via-white/10 to-transparent backdrop-blur-sm border-b border-white/10">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src={BRAND_LOGO_SRC}
              alt={`${BRAND_NAME} logo`}
              className="h-10 w-auto drop-shadow-[0_4px_10px_rgba(0,0,0,0.25)]"
              style={{backgroundColor: "white", borderRadius: "6px"}}
            />
            <span className="text-2xl font-semibold text-white tracking-wide drop-shadow">
              {BRAND_NAME}
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          <ul className="space-y-2">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                      active
                        ? 'bg-white/20 text-white shadow-lg'
                        : 'text-white/70 hover:bg-white/10 hover:text-white hover:shadow-md'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      active ? 'text-white' : 'text-white/70 group-hover:text-white'
                    }`} />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-slate-200 z-10">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 mr-2"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-semibold text-slate-900">
                Dashboard
              </h1>
            </div>

            <div className="flex items-center space-x-3">
              {/* Global Search - Desktop */}
              <div className="hidden md:block w-80">
                <GlobalSearch
                  drivers={searchData.drivers}
                  vehicles={searchData.vehicles}
                  documents={searchData.documents}
                  onNavigate={handleGlobalSearchNavigate}
                  placeholder="Search drivers, vehicles, documents..."
                  className="w-full"
                />
              </div>

              {/* Global Search - Mobile */}
              <button 
                onClick={() => setMobileSearchOpen(true)}
                className="md:hidden p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
              >
                <Search className="h-5 w-5" />
              </button>

              {/* Notifications: real for reviewers (driver applications); others keep the placeholder */}
              {canReviewApplications ? (
                <ApplicationNotificationsBell />
              ) : (
                <button className="relative p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200">
                  <Bell className="h-5 w-5" />
                  <span className="absolute top-1 right-1 h-2 w-2 bg-danger-500 rounded-full animate-pulse"></span>
                </button>
              )}

              {/* User menu */}
              <div className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label="Account menu"
                  aria-expanded={userMenuOpen}
                  className="flex items-center space-x-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                >
                  <User className="h-5 w-5" />
                  <span className="hidden md:block text-sm font-medium text-slate-700">
                    {user?.name}
                  </span>
                </button>
                
                {/* User dropdown menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-slate-200">
                    <div className="px-4 py-2 border-b border-slate-200">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      <p className="text-xs text-primary-600 font-medium capitalize">{user?.role}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors duration-200"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Search Modal */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
            onClick={() => setMobileSearchOpen(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-x-0 top-0 bg-white shadow-lg">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Search</h2>
              <button
                onClick={() => setMobileSearchOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              <GlobalSearch
                drivers={searchData.drivers}
                vehicles={searchData.vehicles}
                documents={searchData.documents}
                onNavigate={(type, data) => {
                  handleGlobalSearchNavigate(type, data);
                  setMobileSearchOpen(false);
                }}
                placeholder="Search drivers, vehicles, documents..."
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLayout;
